import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toastError, toastSuccess } from "../utils/toast";
import { useAuth } from "../hooks";

const Login = () => {
  const navigate = useNavigate();
  const { login, loading, user, isAuthenticated } = useAuth();
  const toastShownRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    document.title = "Login - Shop Kiganjani";
    toastShownRef.current = false;
  }, []);

  const redirectUser = useCallback(
    (role) => {
      const adminRoles = ["super_admin", "admin"];
      if (adminRoles.includes(role)) {
        navigate("/app");
      } else {
        navigate("/shop");
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      redirectUser(user.role);
    }
  }, [isAuthenticated, user, redirectUser]);

  async function handleSubmit(e) {
    e.preventDefault();
    const newErrors = {};

    if (!email.trim()) newErrors.email = "Email or username is required";
    if (!password.trim()) newErrors.password = "Password is required";

    setErrors(newErrors);
    setLoginError("");
    toastShownRef.current = false;

    if (Object.keys(newErrors).length === 0) {
      try {
        const response = await login({
          login: email.trim(),
          password: password.trim(),
        });

        if (response?.data?.user) {
          const userRole = response.data.user.role;
          toastSuccess("Login successful! Welcome back.");

          setTimeout(() => {
            redirectUser(userRole);
          }, 300);
        }
      } catch (err) {
        if (!toastShownRef.current) {
          toastShownRef.current = true;
          toastError(err?.message || "Invalid credentials. Please try again.");
        }
        setLoginError(err?.message || "Invalid credentials. Please try again.");
      }
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-700">
            <FontAwesomeIcon icon={faStore} className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-sm text-gray-600 text-center">
            Login to your account to continue
          </p>
        </div>

        {/* Login Error */}
        {loginError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-600">{loginError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Username/Email Field */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="email"
              className="text-gray-800 font-medium text-base"
            >
              Email or Username
            </Label>
            <Input
              id="email"
              type="text"
              placeholder="Enter your email or username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: undefined }));
                setLoginError("");
                toastShownRef.current = false;
              }}
              className={
                errors.email ? "border-red-500 ring-1 ring-red-500" : ""
              }
            />
            {errors.email && (
              <span className="text-xs text-red-500">{errors.email}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="password"
              className="text-gray-800 font-medium text-base"
            >
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                  setLoginError("");
                  toastShownRef.current = false;
                }}
                className={
                  errors.password
                    ? "border-red-500 ring-1 ring-red-500 pr-10"
                    : "pr-10"
                }
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon
                  icon={showPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.password && (
              <span className="text-xs text-red-500">{errors.password}</span>
            )}
            <div className="flex justify-end">
              <Link
                to="/resetPassword"
                className="text-sm text-blue-900 hover:cursor-pointer font-semibold hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed h-11"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </Button>
        </form>

        {/* Back Link */}
        <div className="mt-4 text-center">
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-blue-800 transition-colors"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Login;
